import { router } from '@inertiajs/react'
import { MessageCircleIcon, ClockIcon } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useState, useEffect, useRef } from 'react'
import * as ActionCable from '@rails/actioncable'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { MetaTabs } from '@/components/meta-tabs'

interface Lead {
  id: number
  name: string
  messenger_username: string | null
}

interface MessengerConnection {
  id: number
  page_name: string
  business_type: string
}

interface Conversation {
  id: number
  psid: string
  lead: Lead
  messenger_connection: MessengerConnection
  last_message_preview: string | null
  last_message_at: string
  unread_count: number
}

interface ConnectedAccounts {
  instagram?: string | null
  messenger?: string | null
  whatsapp?: string | null
}

interface MessengerMessagesIndexProps {
  conversations: Conversation[]
  business_type: string
  connected_accounts: ConnectedAccounts
}

export default function MessengerMessagesIndex({ conversations: initialConversations, business_type, connected_accounts }: MessengerMessagesIndexProps) {
  const [conversations, setConversations] = useState<Conversation[]>(initialConversations)
  const cableRef = useRef<ActionCable.Cable | null>(null)
  const channelRef = useRef<ActionCable.Channel | null>(null)
  const reloadScheduledRef = useRef(false)

  const handleConversationClick = (conversationId: number) => {
    router.visit(`/recruiting/messenger_messages/${conversationId}`)
  }

  // Set up ActionCable connection for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    if (!token) {
      console.error('[ActionCable] No auth token found')
      return
    }

    // Create cable connection
    const cable = ActionCable.createConsumer(`/cable?token=${token}`)
    cableRef.current = cable

    // Subscribe to user messenger conversations channel
    const channel = cable.subscriptions.create(
      {
        channel: 'UserMessengerConversationsChannel',
      },
      {
        connected() {
          console.log('[ActionCable] Connected to user messenger conversations stream')
        },
        disconnected() {
          console.log('[ActionCable] Disconnected from user messenger conversations stream')
        },
        received(data: {
          type: string
          conversation_id: number
          last_message_preview: string
          last_message_at: string
          unread_count: number
          conversation?: Conversation
        }) {
          console.log('[ActionCable] Received conversation update:', data)

          if (data.type === 'conversation_created') {
            // New conversation - add it to the list
            if (data.conversation) {
              setConversations((prev) => {
                // Check if conversation already exists (avoid duplicates)
                if (prev.some((c) => c.id === data.conversation!.id)) {
                  return prev
                }

                // Add new conversation and sort by most recent
                const newConversations = [data.conversation, ...prev]
                newConversations.sort((a, b) =>
                  new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
                )

                return newConversations
              })
            }
          } else if (data.type === 'conversation_updated') {
            setConversations((prev) => {
              // Find the conversation to update
              const conversationIndex = prev.findIndex((c) => c.id === data.conversation_id)

              if (conversationIndex === -1) {
                // Conversation not in list - shouldn't happen with new logic, but reload as fallback
                if (!reloadScheduledRef.current) {
                  reloadScheduledRef.current = true
                  setTimeout(() => {
                    router.reload({ only: ['conversations'] })
                  }, 0)
                }
                return prev
              }

              // Update the conversation
              const updatedConversations = [...prev]
              updatedConversations[conversationIndex] = {
                ...updatedConversations[conversationIndex],
                last_message_preview: data.last_message_preview,
                last_message_at: data.last_message_at,
                unread_count: data.unread_count,
              }

              // Re-sort by last_message_at (most recent first)
              updatedConversations.sort((a, b) =>
                new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
              )

              return updatedConversations
            })
          }
        },
      }
    )
    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        channelRef.current.unsubscribe()
      }
      if (cableRef.current) {
        cableRef.current.disconnect()
      }
    }
  }, [])

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatTime = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true })
    } catch {
      return ''
    }
  }

  return (
    <div className="flex flex-1 flex-col min-w-0">
      <div className="@container/main flex flex-1 flex-col gap-2 min-w-0">
        <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 min-w-0">
          {/* Header */}
          <div className="px-4 lg:px-6">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Meta/Facebook Messages</h1>
              <p className="text-muted-foreground">View and manage your driver recruitment messages via Facebook Messenger</p>
            </div>
            <div className="mt-6">
              <MetaTabs activeTab="messenger" businessType="recruiting" connectedAccounts={connected_accounts} />
            </div>
          </div>

          {/* Conversations List */}
          <div className="px-4 lg:px-6">
            {conversations.length === 0 ? (
              <Card>
                <CardHeader>
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <MessageCircleIcon className="mb-4 size-12 text-muted-foreground" />
                    <CardTitle>No conversations yet</CardTitle>
                    <CardDescription className="mt-2">
                      Once you connect your recruiting Facebook Messenger page and receive messages, they will appear here.
                    </CardDescription>
                  </div>
                </CardHeader>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {conversations.map((conversation) => (
                      <button
                        key={conversation.id}
                        onClick={() => handleConversationClick(conversation.id)}
                        className="w-full p-4 text-left transition-colors hover:bg-muted/50"
                        data-testid={`conversation-${conversation.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <Avatar className="size-12">
                            <AvatarFallback className="bg-gradient-to-br from-[#0084ff] to-[#006bd6] text-white">
                              {getInitials(conversation.lead.name)}
                            </AvatarFallback>
                          </Avatar>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <h3 className="font-semibold truncate">{conversation.lead.name}</h3>
                                {conversation.lead.messenger_username && (
                                  <span className="text-sm text-muted-foreground truncate">
                                    @{conversation.lead.messenger_username}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {conversation.unread_count > 0 && (
                                  <Badge variant="default" className="bg-[#0084ff]">
                                    {conversation.unread_count}
                                  </Badge>
                                )}
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <ClockIcon className="size-3" />
                                  {formatTime(conversation.last_message_at)}
                                </div>
                              </div>
                            </div>

                            <p className="text-sm text-muted-foreground truncate">
                              {conversation.last_message_preview || 'No messages yet'}
                            </p>

                            <div className="mt-2 text-xs text-muted-foreground">
                              Page: {conversation.messenger_connection.page_name}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
